/**
 * /auth/workspace-invite — the invitations waiting on this login.
 *
 * WHY THIS PAGE EXISTS (2026-08-25). Inviting an address that already belonged
 * to another workspace used to return a flat 409 and send nothing. Waldo, Beno
 * and Paul's own elreign.sixtyfive@ address all hit it; on the recorded prod
 * walkthrough the failure toast vanished before it could be read, so the invite
 * looked like it had worked and the invitee was never told anything.
 *
 * The invite now writes an `invited` membership row and emails an offer. This
 * page is where that offer is taken or refused. Nothing here changes which
 * workspace the person is currently in: accepting ADDS one to the switcher, and
 * moving is a separate, deliberate act.
 *
 * It carries no tenant parameter on purpose. The server decides what is pending
 * for the signed-in user, so a tampered link cannot surface someone else's
 * invitation.
 */

import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AuthLayout } from '../../components/layouts/auth-layout';
import { useMyTenants, useRespondToWorkspaceInvite } from '../../lib/api/me-tenants';
import type { PendingWorkspaceInvite } from '../../lib/api/me-tenants';

export const Route = createFileRoute('/auth/workspace-invite')({
  component: WorkspaceInvitePage,
});

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrator',
  compliance: 'Compliance officer',
  relationship_manager: 'Relationship manager',
  director: 'Director',
  user: 'Member',
};

function roleLabel(role: string): string {
  return ROLE_LABEL[role] ?? role.replace(/_/g, ' ');
}

function WorkspaceInvitePage() {
  const { data, isLoading, isError } = useMyTenants();
  const respond = useRespondToWorkspaceInvite();
  // Which tenant this click is for, so two invitations do not both show busy.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [settled, setSettled] = useState<Record<string, 'accept' | 'decline'>>({});
  const [failure, setFailure] = useState<string | null>(null);

  const invites = data?.pendingInvites ?? [];
  const open = invites.filter((i) => !settled[i.tenantId]);

  function act(invite: PendingWorkspaceInvite, decision: 'accept' | 'decline') {
    if (respond.isPending) return;
    setPendingId(invite.tenantId);
    setFailure(null);
    respond.mutate(
      { tenantId: invite.tenantId, decision },
      {
        onSuccess: () => {
          setSettled((s) => ({ ...s, [invite.tenantId]: decision }));
          setPendingId(null);
        },
        onError: () => {
          // Honest and specific. The Klaprops false-copy class: a failed action
          // that renders as anything other than a failure is worse than the
          // failure.
          setFailure(
            decision === 'accept'
              ? 'We could not accept that invitation. Nothing has changed. Please try again.'
              : 'We could not decline that invitation. Nothing has changed. Please try again.',
          );
          setPendingId(null);
        },
      },
    );
  }

  const justAccepted = Object.values(settled).some((d) => d === 'accept');

  return (
    <AuthLayout
      eyebrow="Invitations"
      heading={open.length > 0 ? 'You have been invited.' : 'Invitations.'}
      sub={
        open.length > 0
          ? 'Accepting adds this workspace to your account. The workspace you use now stays exactly as it is, and you can move between them whenever you like.'
          : undefined
      }
    >
      {isLoading && (
        <div style={{ fontSize: 14, color: 'var(--ink-3)', textAlign: 'center', padding: '18px 0' }}>
          Checking for invitations.
        </div>
      )}

      {isError && (
        <div
          role="alert"
          style={{
            padding: '12px 14px',
            borderRadius: 9,
            background: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            color: '#991B1B',
            fontSize: 13.5,
            lineHeight: 1.5,
          }}
        >
          We could not load your invitations just now. This is not a sign that
          anything is wrong with your account. Please refresh in a moment.
        </div>
      )}

      {failure !== null && (
        <div
          role="alert"
          style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 9,
            background: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            color: '#991B1B',
            fontSize: 13.5,
            lineHeight: 1.5,
          }}
        >
          {failure}
        </div>
      )}

      {open.map((invite) => {
        const busy = respond.isPending && pendingId === invite.tenantId;
        return (
          <div
            key={invite.tenantId}
            style={{
              padding: '16px 18px',
              borderRadius: 10,
              border: '1px solid var(--hairline-2)',
              background: 'var(--surface-2)',
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>
              {invite.tenantName ?? 'An Elreign workspace'}
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 14 }}>
              You have been invited as {roleLabel(invite.role)}.
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => act(invite, 'accept')}
                disabled={busy}
                style={{
                  padding: '9px 18px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'var(--brand-primary, var(--primary-600))',
                  color: '#F4EFE5',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? 'Working…' : 'Accept'}
              </button>
              <button
                type="button"
                onClick={() => act(invite, 'decline')}
                disabled={busy}
                style={{
                  padding: '9px 18px',
                  borderRadius: 8,
                  border: '1px solid var(--hairline-2)',
                  background: 'transparent',
                  color: 'var(--ink-2)',
                  fontSize: 14,
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? 0.7 : 1,
                }}
              >
                Decline
              </button>
            </div>
          </div>
        );
      })}

      {!isLoading && !isError && open.length === 0 && (
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 9,
            background: 'var(--surface-2)',
            fontSize: 13.5,
            color: 'var(--ink-2)',
            lineHeight: 1.55,
            textAlign: 'center',
          }}
        >
          {justAccepted
            ? 'Done. The workspace is on your account now, and you can switch to it from the workspace menu whenever you are ready.'
            : 'Nothing is waiting for you. If you were expecting an invitation, ask whoever sent it to check the address they used.'}
        </div>
      )}

      <div
        style={{
          marginTop: 22,
          paddingTop: 16,
          borderTop: '1px solid var(--hairline)',
          fontSize: 13.5,
          color: 'var(--ink-3)',
          textAlign: 'center',
        }}
      >
        <Link to="/app/dashboard" style={{ color: 'var(--primary-600)', fontWeight: 500 }}>
          Back to your dashboard
        </Link>
      </div>
    </AuthLayout>
  );
}
